import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Breadcrumbs, 
  Link, 
  Divider, 
  Paper,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Assignment, 
  Gavel, 
  AccountBalance, 
  Payment, 
  Security, 
  VerifiedUser, 
  Public, 
  Copyright, 
  Block,
  Link as LinkIcon,
  Chat,
  Person
} from '@mui/icons-material';

const TermsAndConditions: React.FC = () => {
  const theme = useTheme();

  const sections = [
    {
      id: 'acceptance',
      title: 'Acceptance of Terms',
      icon: Assignment,
      content: `By accessing and using our website, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, you must not use our website or services.`
    },
    {
      id: 'services',
      title: 'Services Description',
      icon: Gavel,
      content: `Chishty UK Immigration Law & Visa Advisor provides immigration consultation, visa application assistance, and related legal services. We strive to ensure accuracy and quality in our services, but outcomes of immigration applications depend on various factors including applicant eligibility, documentation, and current immigration policies. Our service fees are for professional advisory services and do not include government application fees, which must be paid directly to the relevant authorities.`
    },
    {
      id: 'user-obligations',
      title: 'User Obligations',
      icon: Person,
      content: `When using our services, you agree to: provide accurate and complete information, respond to requests for additional information promptly, notify us of any changes in your circumstances that may affect your application, and pay all applicable fees. You acknowledge that failure to meet these obligations may negatively impact the outcome of your application.`
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual Property Rights',
      icon: Copyright,
      content: `All content on this website, including but not limited to text, graphics, logos, images, audio clips, digital downloads, data compilations, and software, is the property of Chishty UK Immigration Law & Visa Advisor or its content suppliers and is protected by international copyright laws. The compilation of all content on this site is the exclusive property of our company and is protected by international copyright laws.`
    },
    {
      id: 'limitations',
      title: 'Limitations of Liability',
      icon: Block,
      content: `To the fullest extent permitted by applicable law, Chishty UK Immigration Law & Visa Advisor shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses, resulting from your access to or use of or inability to access or use the services. We do not guarantee the outcome of any immigration application as decisions are made by the relevant immigration authorities.`
    },
    {
      id: 'confidentiality',
      title: 'Confidentiality and Data Protection',
      icon: Security,
      content: `We are committed to protecting your privacy and maintaining the confidentiality of your personal information. All information provided to us during the course of our services will be treated as confidential and will only be used for the purpose of providing our services to you. Please refer to our Privacy Policy for more detailed information on how we collect, use, and protect your data.`
    },
    {
      id: 'payments',
      title: 'Payments and Refunds',
      icon: Payment,
      content: `Payment for our services must be made in advance unless otherwise agreed. We accept payments via bank transfer and major credit cards. Refunds are subject to our refund policy and are generally not provided once work on your case has commenced. In the case of cancellation prior to any work being done, a partial refund may be issued at our discretion, less an administrative fee.`
    },
    {
      id: 'termination',
      title: 'Termination of Services',
      icon: AccountBalance,
      content: `We reserve the right to terminate our services if: you breach these terms and conditions, you provide false or misleading information, you engage in abusive behavior towards our staff, or payment for services is not received as agreed. In such cases, you may not be entitled to a refund for services already provided.`
    },
    {
      id: 'external-links',
      title: 'Links to Third-Party Websites',
      icon: LinkIcon,
      content: `Our website may contain links to external websites that are not operated by us. We have no control over, and assume no responsibility for the content, privacy policies, or practices of any third-party websites. We strongly advise you to review the terms and privacy policy of every site you visit.`
    },
    {
      id: 'governing-law',
      title: 'Governing Law',
      icon: Public,
      content: `These Terms and Conditions shall be governed by and construed in accordance with the laws of Pakistan, without regard to its conflict of law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.`
    },
    {
      id: 'changes',
      title: 'Changes to Terms',
      icon: VerifiedUser,
      content: `We reserve the right to modify these terms at any time. We will provide notice of any significant changes. Your continued use of our website and services following the posting of changes constitutes your acceptance of such changes.`
    }
  ];

  return (
    <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: 'background.default' }}>
      <Container maxWidth="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 4 }}>
          <Link component={RouterLink} to="/" color="inherit">
            Home
          </Link>
          <Typography color="text.primary">Terms & Conditions</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography 
            variant="h1" 
            component="h1" 
            sx={{ 
              fontSize: { xs: '2.5rem', md: '3.5rem' }, 
              fontWeight: 700,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Terms & Conditions
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ 
              maxWidth: '800px', 
              mx: 'auto',
              mb: 2 
            }}
          >
            Last Updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </Typography>
          <Divider sx={{ width: '60px', mx: 'auto', borderWidth: 2, borderColor: theme.palette.primary.main, my: 3 }} />
          <Typography 
            variant="body1" 
            sx={{ 
              maxWidth: '800px', 
              mx: 'auto' 
            }}
          >
            Please read these terms and conditions carefully before using our services. By using the services provided by Chishty UK Immigration Law & Visa Advisor, you agree to be bound by these terms and conditions.
          </Typography>
        </Box>

        {/* Table of Contents */}
        <Paper elevation={0} sx={{ p: 4, mb: 6, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
            Table of Contents
          </Typography>
          <List sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
            {sections.map((section) => (
              <ListItem 
                key={section.id} 
                component="a" 
                href={`#${section.id}`} 
                sx={{ 
                  cursor: 'pointer',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.03)'
                  }
                }}
              >
                <ListItemIcon>
                  <section.icon color="primary" />
                </ListItemIcon>
                <ListItemText primary={section.title} />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Terms & Conditions Content */}
        <Box sx={{ mb: 6 }}>
          {sections.map((section) => (
            <Box key={section.id} id={section.id} sx={{ mb: 6, scrollMarginTop: '80px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                <section.icon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {section.title}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ ml: { xs: 0, sm: 6 }, pl: { xs: 0, sm: 1 } }}>
                {section.content}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Contact Section */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            borderRadius: 2, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            background: `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.primary.light}20)`,
            border: `1px solid ${theme.palette.primary.main}20`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
            <Chat sx={{ fontSize: 32, color: theme.palette.primary.main }} />
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Questions or Concerns?
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            If you have any questions about these Terms and Conditions, please contact us at:
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontWeight: 500 }}>
            Chishty UK Immigration Law & Visa Advisor<br />
            1 Turner Road Atif Centre<br />
            High Court Lahore<br /><br />
            Email: contact@chishtyukimmigrationlaw.info
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsAndConditions; 